import React from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, Link } from '@mui/material';
import { ColorsV2 } from 'app/constants/color';

const formatDateTime = text => {
  const dateTimeRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/; // Match ISO 8601 date-time strings
  return text.replace(dateTimeRegex, match => {
    const date = new Date(match);
    return date.toLocaleString('en-SE', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
      hour12: false, // Use 24-hour format
    });
  });
};

const formatLinks = text => {
  const linkRegex = /<a href="(.*?)">(.*?)<\/a>/g; // Match <a> tags with href and inner text
  const parts = [];
  let lastIndex = 0;

  text.replace(linkRegex, (match, href, innerText, offset) => {
    // Add plain text before the link
    if (offset > lastIndex) {
      parts.push(text.slice(lastIndex, offset));
    }

    // Add the MUI Link
    parts.push(
      <Link
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        key={offset}
        sx={{ color: ColorsV2.acent, textDecoration: 'underline' }}
      >
        {innerText}
      </Link>
    );
    lastIndex = offset + match.length;
  });

  // Add remaining plain text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const parseMessage = message => {
  const lines = message.split(/\n/); // Split message by newlines
  const sections = [];
  let currentList = null;

  lines.forEach((line, index) => {
    if (line.includes('<br>')) {
      // Identify a list item
      const items = line
        .split('<br>')
        .map(item => item.trim())
        .filter(item => item);

      if (!currentList) {
        // If no current list, create one
        const title = lines[index - 1]?.trim(); // The line before the first list item is the title
        if (title && sections.length > 0 && sections[sections.length - 1].type === 'text') {
          sections.pop(); // Remove duplicate title from text sections
        }
        currentList = {
          type: 'list',
          title,
          items: [],
        };
        sections.push(currentList);
      }

      currentList.items.push(...items); // Add split items to the list
    } else {
      // If no <br>, treat as normal text
      if (currentList) {
        currentList = null; // Close current list
      }
      sections.push({ type: 'text', content: line.trim() });
    }
  });

  return sections;
};

const Message = ({ message, className, sx }) => {
  const parsedMessage = parseMessage(message);

  return (
    <Box
      className={className}
      sx={{
        ...sx,
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        fontFamily: 'monospace',
        color: ColorsV2.white,
        lineHeight: 1.8,
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'pre-wrap', // Ensure text wraps correctly
      }}
    >
      {parsedMessage.map((section, index) => {
        if (section.type === 'list') {
          return (
            <Box
              key={index}
              sx={{
                marginBottom: 3,
                padding: 2,
                borderLeft: `4px solid ${ColorsV2.acent}`,
                borderRadius: '4px',
              }}
            >
              <Typography
                variant='subtitle1'
                gutterBottom
                sx={{
                  color: ColorsV2.white,
                  fontWeight: 'bold',
                  marginBottom: '8px',
                }}
              >
                {section.title}
              </Typography>
              <List>
                {section.items.map((item, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemIcon sx={{ minWidth: 'auto', marginRight: 1 }}>
                      <CircleIcon fontSize='small' sx={{ color: ColorsV2.acent }} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ sx: { color: ColorsV2.white } }}
                      primary={formatLinks(formatDateTime(item))}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        }
        return (
          <Typography
            key={index}
            paragraph
            sx={{
              marginBottom: 2,
              color: ColorsV2.white,
            }}
          >
            {formatLinks(formatDateTime(section.content))}
          </Typography>
        );
      })}
    </Box>
  );
};

export default Message;
